// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the free local check before a draft goes to the assistant (action
// chips, docs/konvo-assistant/action-chips.md). A draft is only worth a model
// call when, in one sentence, it names a colleague (an @mention or a known
// name) AND hands them something: an ask ("can you", "please", "we might want
// to", Afrikaans "kan jy", "sal jy") or a commitment in their name ("Markus
// will look at it tomorrow", "Markus sal kyk"). Pure, so it is tested on its own.

export interface DraftHandover {
	/** The name as written in the draft, without the @. */
	name: string;
	/** Whether the person was @mentioned. */
	mention: boolean;
}

/** Asks and handovers that can sit anywhere in the sentence. */
const ASK_PATTERNS: ReadonlyArray<RegExp> = [
	/\b(?:can|could|would|will|wil) (?:you|u|ya|we)\b/,
	/\b(?:please|pls|plz|asseblief|asb)\b/,
	/\b(?:need|want|needs|wants) (?:you|u) to\b/,
	/\bwe (?:might|may|should|could|must|need to|have to)\b/,
	/\b(?:might|may) want to\b/,
	/\bmake sure\b/,
	/\b(?:take|have) a look\b/,
	/\b(?:look|looking) (?:at|into)\b/,
	/\bfollow(?:ing)? up\b/,
	/\bfor you to\b/,
	// Afrikaans
	/\b(?:kan|sal|wil|moet) (?:jy|julle|u|ons)\b/,
	/\b(?:jy|julle) (?:moet|kan|sal)\b/,
	/\b(?:gaan|sal) (?:kyk|kyk na|na .{1,40} kyk)\b/,
	/\bkyk (?:gou|asb|asseblief)\b/,
];

/** A commitment made in the named person's name: "Markus will …", "Markus is going to …", "Markus sal …". */
function commitmentAfter(name: string): RegExp {
	return new RegExp(
		`(?:^|[^\\p{L}])@?${escapeRegExp(name)}(?:'ll| will| is going to| can| should| must| needs to| sal| gaan| moet| kan)\\b`,
		'u',
	);
}

function escapeRegExp(value: string): string {
	return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function normaliseDraft(value: string): string {
	return value.toLowerCase().replace(/[‘’]/g, "'").replace(/\s+/g, ' ').trim();
}

/** Sentences of the draft, lowercased; a line break or . ! ? ends one. */
function sentences(text: string): Array<string> {
	return normaliseDraft(text.replace(/([.!?])\s+/g, '$1\n'))
		.split(/[\n]|(?<=[.!?])\s/)
		.map((part) => part.trim())
		.filter(Boolean);
}

/** The names to look for: full names and first names of at least two letters, longest first. */
function nameKeys(names: ReadonlyArray<string>): Array<string> {
	const keys = new Set<string>();
	for (const raw of names) {
		const name = normaliseDraft(raw);
		if (name.length < 2) continue;
		keys.add(name);
		const first = name.split(' ')[0] ?? '';
		if (first.length >= 2) keys.add(first);
	}
	return [...keys].sort((a, b) => b.length - a.length);
}

function namedIn(sentence: string, key: string): boolean {
	return new RegExp(`(?:^|[^\\p{L}])@?${escapeRegExp(key)}(?![\\p{L}])`, 'u').test(sentence);
}

/**
 * The person a draft hands work to, or null when it does not look like a
 * handover. `names` are the people it could be (chat members, colleagues).
 */
export function findHandover(text: string, names: ReadonlyArray<string>): DraftHandover | null {
	if (!text.trim() || text.trim().startsWith('/')) return null;
	const keys = nameKeys(names);
	for (const sentence of sentences(text)) {
		const asks = ASK_PATTERNS.some((pattern) => pattern.test(sentence));
		const mentions = [...sentence.matchAll(/(?:^|[^\p{L}\d])@([\p{L}][\p{L}'-]*)/gu)].map((match) => match[1]!);
		if (asks && mentions.length > 0) return {name: mentions[0]!, mention: true};
		for (const key of keys) {
			if (!namedIn(sentence, key)) continue;
			if (asks || commitmentAfter(key).test(sentence)) {
				return {name: key, mention: sentence.includes(`@${key}`)};
			}
		}
		for (const name of mentions) {
			if (commitmentAfter(name).test(sentence)) return {name, mention: true};
		}
	}
	return null;
}

/**
 * Whether a suggestion made from `askedText` still fits the draft as it is now:
 * its quote is still there, or (with no quote) the draft still starts the way
 * it did. An emptied draft never holds.
 */
export function draftStillHolds(quote: string | null, askedText: string, text: string): boolean {
	const current = normaliseDraft(text);
	if (!current) return false;
	if (quote?.trim()) return current.includes(normaliseDraft(quote));
	const asked = normaliseDraft(askedText);
	if (!asked) return false;
	const keep = asked.slice(0, Math.max(12, Math.floor(asked.length * 0.8)));
	return current.startsWith(keep);
}

/**
 * The draft without the words of chips already used or dismissed, so a second
 * handover in the same draft can get its own chip. Matching ignores case and
 * spacing; words that are no longer there are simply not removed.
 */
export function withoutHandled(text: string, handled: ReadonlyArray<string>): string {
	let rest = text;
	for (const quote of handled) {
		const words = quote.trim().split(/\s+/).filter(Boolean).map(escapeRegExp);
		if (words.length === 0) continue;
		rest = rest.replace(new RegExp(words.join('\\s+'), 'i'), ' ');
	}
	return rest.replace(/[ \t]{2,}/g, ' ').trim();
}
